import Vue, { CreateElement } from 'src/vue'
import { Component } from 'vue-property-decorator'

import { Subtitle1 } from '@src/elements/typography/typography'
import { DateFormat, TimeSwitcher } from '@src/elements/time-switch/time-switch'
import ApiClient from '@src/lib/api_client'
import { getCurrentWeekPeriods } from '@src/util/functions'
import { API } from '@src/lib/api_path'
import { Divider } from '@src/elements/divider/divider'
import { convertDateTo } from '@src/util/date-format'
import { getAreaSplineChartOptions } from '@src/util/area-chart-options'
import { DateFilterType } from '@src/elements/time-switch/date-filter-type'
import VueApexCharts from 'vue-apexcharts'

Vue.use(VueApexCharts)

Vue.component('apexchart', VueApexCharts)

import './rpe.sass'
import { getChartRef } from '@src/components/chart/utils'

type IntensityRatingResponse = { periods: IntensityRatingPeriod[] }
type IntensityRatingPeriod = {
  from: string
  to: string
  intensity_rating: number
  workouts: number
}

@Component
export class RPEStatistics extends Vue {

  private timeFrame: string = 'week'

  private apiClient: ApiClient

  private rpeAverages: IntensityRatingPeriod[] = []

  private chartOptions: any = null
  private series: any = []

  constructor () {
    super()
    this.apiClient = new ApiClient(API.TRAINERS_API.CLUB_STATS_AVERAGE_INTENSITY_RATING)
  }

  mounted () {
    window.addEventListener('resize', this.resize)
  }

  beforeDestroy () {
    window.removeEventListener('resize', this.resize)
  }

  resize () {
    const { dateLabels } = this.getChartConfig()
    this.chartOptions = this.buildChartOptions(dateLabels)
    this.forceChartUpdate()
  }

  changeTimeFrame (timeFrame: string, periods: any[]) {
    return this.loadRPEStats(timeFrame, periods)
  }

  getChartConfig () {
    let maxValue: number = this.rpeAverages.length > 0 ? this.rpeAverages.reduce((prev, current) => (prev.intensity_rating > current.intensity_rating) ? prev : current).intensity_rating : 0
    let dateLabels: any[] = []
    // rpe aggregated values per day/month
    let values: any[] = []

    // date labels will be [null, Monday ... Sunday, null] to be able to show the first and last day fully (same width as the other days)
    // and the series [0, value1 ... valueX, 0] to give a curve effect on the edges (line will go down at the edges)
    this.rpeAverages.forEach(day => {
      dateLabels.push(convertDateTo(day.from, DateFormat[this.timeFrame.toUpperCase()]))
      values.push(day.intensity_rating)
    })

    const labels = dateLabels.map((label) => {
      const multilineLabel = label.split(',')
      if (multilineLabel.length > 1) return multilineLabel
      return label
    })

    return { maxValue, dateLabels: labels, values }
  }

  render (h: CreateElement) {
    if (!this.chartOptions) return ''
    return <div class='column rpe'>
      <Subtitle1>{this.$t('PAGE.CLUB.CLUB_STATISTICS.AGGREGATED_RPE')}</Subtitle1>

      <TimeSwitcher dateFilters={[DateFilterType.WEEK, DateFilterType.MONTH, DateFilterType.YEAR]} change={this.changeTimeFrame} align='right' allowFutureDate={false} />

      <div id={'rpe-statistics-chart'}>
        <apexchart
          ref='rpe-statistics-chart'
          type={this.chartOptions.chart.type}
          options={this.chartOptions}
          height={this.chartOptions.chart.height}
          series={this.series} />
      </div>

      <Divider />
    </div>
  }

  beforeMount () {
    return this.loadRPEStats(this.timeFrame, getCurrentWeekPeriods())
  }

  forceChartUpdate () {
    const chart = getChartRef(this.$refs, 'rpe-statistics-chart')
    // on the first load the chart is not rendered yet, it starts with the current series and options
    if (!chart) return

    chart.updateSeries(this.series, true)
    chart.updateOptions(this.chartOptions, true, true, true).then(() => {
      this.$forceUpdate()
    })
  }

  buildChartOptions (dateLabels: any) {
    const options = getAreaSplineChartOptions(dateLabels, { padding: { left: 25, bottom: 30 } }, 10)
    return {
      ...options,
      tooltip: {
        ...options.tooltip,
        x: {
          formatter: function (value, { dataPointIndex }) {
            return dateLabels[dataPointIndex]
          }
        }
      }
    }
  }

  private loadRPEStats (timeFrame: string, periods: any[]) {
    return this.apiClient.get<IntensityRatingResponse>('', { periods: periods })
      .then((response) => {
        this.timeFrame = timeFrame
        this.rpeAverages = response.periods

        const { dateLabels, values } = this.getChartConfig()
        this.chartOptions = this.buildChartOptions(dateLabels)
        this.series = [
          {
            id: this.$t('PAGE.CLUB.CLUB_STATISTICS.AGGREGATED_RPE'),
            name: this.$t('PAGE.CLUB.CLUB_STATISTICS.AGGREGATED_RPE'),
            data: values,
            color: '#a7cff3'
          }
        ]
        this.forceChartUpdate()
      })
      .catch((error) => {
        console.error('failed to retrieve club stats - rpe', error)
      })
  }
}
